'use client'
import { Assessments } from '@crystal-eyes/types'
import SectionCard from '../../components/SectionCard'
import Icon, { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import styles from './BigFive.module.scss'
import assessmentStyles from './Assessments.module.scss'
import useIsOwnProfile from '@crystal-eyes/hooks/profiles/context/useIsOwnProfile'
import Link from 'next/link'
import { getRawScorePercentile } from '@dashboard/lib/bigFive'
import useTakingAssessment from '../../hooks/useTakingAssessment'

interface Identity {
  id: string
}

export default function BigFive({
  identity,
  assessments,
}: {
  identity: Identity
  assessments?: Assessments
}) {
  const { data: isOwnProfile } = useIsOwnProfile({ identity })

  const {
    assessmentState: { setTaking: setTakingAssessment },
  } = useTakingAssessment()

  const bigFiveResults = assessments?.bigFive

  const extraversion =
    typeof bigFiveResults?.extraversion !== 'undefined' &&
    Math.round(
      getRawScorePercentile('Extraversion', bigFiveResults.extraversion) * 100,
    )

  const agreeableness =
    typeof bigFiveResults?.agreeableness !== 'undefined' &&
    Math.round(
      getRawScorePercentile('Agreeableness', bigFiveResults.agreeableness) *
        100,
    )

  const conscientiousness =
    typeof bigFiveResults?.conscientiousness !== 'undefined' &&
    Math.round(
      getRawScorePercentile(
        'Conscientiousness',
        bigFiveResults.conscientiousness,
      ) * 100,
    )

  const emotionalStability =
    typeof bigFiveResults?.emotionalStability !== 'undefined' &&
    Math.round(
      getRawScorePercentile(
        'Emotional Stability',
        bigFiveResults.emotionalStability,
      ) * 100,
    )

  const openness =
    typeof bigFiveResults?.openness !== 'undefined' &&
    Math.round(getRawScorePercentile('Openness', bigFiveResults.openness) * 100)

  if (!isOwnProfile && !bigFiveResults) return null

  return (
    <SectionCard
      cardId="big_five"
      icon={Icons.BigFive}
      title="Big Five Assessment"
      sidebarTitle="Big five"
      showPrint={true}
      className={`${assessmentStyles.assessment} ${styles.bigFive}`}
    >
      {!bigFiveResults && (
        <div className="take-assessment-cta">
          <div className="visualization">
            <div className="bars">
              <div className="bar CE Parented disc-background" />
              <div className="bar CE Parented disc-background" />
              <div className="bar CE Parented disc-background" />
              <div className="bar CE Parented disc-background" />
              <div className="bar CE Parented disc-background" />
            </div>
          </div>

          <div className="cta">
            <h4>Find out your Big Five trait scores</h4>

            <p>
              Answer a few additional questions to learn more about your
              personality.
            </p>

            <button
              className="CE action-button"
              onClick={evt => {
                evt.preventDefault()
                setTakingAssessment('bigFive')
              }}
            >
              Start Test
            </button>
          </div>
        </div>
      )}

      {bigFiveResults && (
        <>
          <div className="assessment-results">
            <div className="trait-measurement">
              <h4>Extraversion</h4>
              <p>
                Extroversion is a measure of how outgoing, talkative, and
                people-oriented someone is.
              </p>

              <TraitBar trait="extraverted" percentage={extraversion || 0} />
            </div>

            <div className="trait-measurement">
              <h4>Agreeableness</h4>
              <p>
                Agreeableness measures how easily a person connects with others.
              </p>

              <TraitBar trait="agreeable" percentage={agreeableness || 0} />
            </div>

            <div className="trait-measurement">
              <h4>Conscientiousness</h4>
              <p>
                Conscientiousness measures the thoughtfulness and organization
                of a person.
              </p>

              <TraitBar
                trait="conscientious"
                percentage={conscientiousness || 0}
              />
            </div>

            <div className="trait-measurement">
              <h4>Emotional Stability</h4>
              <p>
                Emotional Stability is a measure of how calm and stable a person
                is during stress.
              </p>

              <TraitBar
                trait="emotionally stable"
                percentage={emotionalStability || 0}
              />
            </div>

            <div className="trait-measurement">
              <h4>Openness</h4>
              <p>
                Openness is the measure of how open to experience, open-minded,
                and imaginative an individual is.
              </p>

              <TraitBar trait="open" percentage={openness || 0} />
            </div>
          </div>

          {isOwnProfile && (
            <div className="retake-cta">
              <Link
                href="#"
                onClick={evt => {
                  evt.preventDefault()
                  setTakingAssessment('bigFive')
                }}
              >
                Retake the Big Five Test
              </Link>
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}

const TraitBar = ({
  trait,
  percentage,
}: {
  trait: string
  percentage: number
}) => (
  <div
    className="result-bar"
    style={{ gridTemplateColumns: `${percentage}% auto` }}
  >
    <div className="filled CE Parented disc-background-light">
      {percentage}%
    </div>

    <div className="CE tooltip">
      <Icon
        icon={Icons.Info}
        style={{ color: '#5e5e5e' }}
        className={styles.icon}
      />

      <div className="tooltip-content">
        You are more {trait} than <b>{percentage}%</b> of the people who took
        this test
      </div>
    </div>
  </div>
)
